import React, { useState, useEffect, useCallback } from 'react';
import { FaTrash, FaSearch, FaFilter, FaDesktop, FaMobileAlt, FaTabletAlt, FaCheckCircle, FaExclamationCircle, FaUserSecret, FaHistory } from 'react-icons/fa';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import StatCard, { StatGrid } from '../../components/ui/StatCard';
import FilterCard from '../../components/ui/FilterCard';
import DataTable, { TableEmpty } from '../../components/ui/DataTable';
import { useToast } from '../../context/ToastContext';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const LoginHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const toast = useToast();

  const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/login-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load login history');
      const data = await res.json();
      setHistory(data.history || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleDelete = async (id) => {
    if (!window.confirm('Move this login record to Recycle Bin?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/login-history/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Record moved to Recycle Bin');
      setHistory(prev => prev.filter(h => h._id !== id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Move ${selectedIds.length} records to Recycle Bin?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/login-history/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedIds })
      });
      if (!res.ok) throw new Error('Bulk delete failed');
      toast.success(`${selectedIds.length} records moved to Recycle Bin`);
      setSelectedIds([]);
      fetchHistory();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filtered = history.filter(item => {
    const matchesSearch = item.userName?.toLowerCase().includes(search.toLowerCase()) ||
      item.userEmail?.toLowerCase().includes(search.toLowerCase()) ||
      item.ip?.toLowerCase().includes(search.toLowerCase()) ||
      item.os?.toLowerCase().includes(search.toLowerCase()) ||
      item.browser?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getDeviceIcon = (device) => {
    if (device === 'Mobile') return <FaMobileAlt className="w-4 h-4 text-gray-400" />;
    if (device === 'Tablet') return <FaTabletAlt className="w-4 h-4 text-gray-400" />;
    return <FaDesktop className="w-4 h-4 text-gray-400" />;
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filtered.map(i => i._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  if (loading) {
    return <SkeletonLoader variant="page" />;
  }

  const successCount = history.filter(h => h.status === 'success').length;
  const failedCount = history.filter(h => h.status === 'failed').length;
  const impersonatedCount = history.filter(h => h.isImpersonated).length;

  return (
    <PageLayout>
      <PageHeader
        title="Login History"
        description="Monitor user access and administrative impersonations"
        icon={FaHistory}
        badge={
          <span className="inline-flex items-center px-4 py-2 rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-sm font-medium border border-primary-100 dark:border-primary-800">
            {history.length} Total Records
          </span>
        }
      />

      <StatGrid>
        <StatCard label="Total Records" value={history.length} icon={FaHistory} />
        <StatCard label="Successful" value={successCount} icon={FaCheckCircle} iconBg="bg-green-100 dark:bg-green-900/30" iconColor="text-green-600 dark:text-green-400" valueColor="text-green-600" />
        <StatCard label="Failed" value={failedCount} icon={FaExclamationCircle} iconBg="bg-red-100 dark:bg-red-900/30" iconColor="text-red-600 dark:text-red-400" valueColor="text-red-600" />
        <StatCard label="Impersonated" value={impersonatedCount} icon={FaUserSecret} iconBg="bg-purple-100 dark:bg-purple-900/30" iconColor="text-purple-600 dark:text-purple-400" />
      </StatGrid>

      <FilterCard>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, email, IP, OS or browser..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-modern pl-11 py-2.5 text-sm"
            />
          </div>
          <div className="relative min-w-[180px]">
            <FaFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="input-modern pl-11 py-2.5 text-sm cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      </FilterCard>

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 rounded-xl">
          <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
            {selectedIds.length} record{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button onClick={handleBulkDelete} className="flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors">
            <FaTrash className="w-4 h-4" />
            Move to Recycle Bin
          </button>
        </div>
      )}

      {error ? (
        <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-center">
          <p className="text-red-600 dark:text-red-400 font-medium mb-4">{error}</p>
          <button onClick={fetchHistory} className="btn-premium !py-2.5">Try Again</button>
        </div>
      ) : (
        <DataTable>
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <th className="px-4 py-4 w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selectedIds.length === filtered.length}
                    onChange={handleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold">User</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Environment</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">IP Address</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Login Time</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filtered.length === 0 ? (
                <TableEmpty colSpan={7} icon={FaHistory} preset="search" title="No Records Found" description="No login records match your search criteria." />
              ) : filtered.map((item) => {
                const isSelected = selectedIds.includes(item._id);
                return (
                  <tr key={item._id} className={`hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors ${isSelected ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={isSelected} onChange={() => handleSelectOne(item._id)} className="rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center text-sm font-semibold relative">
                          {item.userName?.[0]?.toUpperCase()}
                          {item.isImpersonated && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center border-2 border-white dark:border-dark-800" title="Admin Impersonation">
                              <FaUserSecret className="w-2 h-2 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            {item.userName}
                            {item.isImpersonated && <span className="badge badge-info text-[10px]">Impersonated</span>}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{item.userEmail}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        {getDeviceIcon(item.device)}
                        {item.os}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">Browser: {item.browser}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-dark-700 px-2 py-1 rounded-lg">
                        {item.ip || '0.0.0.0'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(item.loginTime)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${item.status === 'success' ? 'badge-success' : 'badge-error'} flex items-center gap-1 w-fit`}>
                        {item.status === 'success' ? <FaCheckCircle className="w-3 h-3" /> : <FaExclamationCircle className="w-3 h-3" />}
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DataTable>
      )}
    </PageLayout>
  );
};

export default LoginHistory;
